import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Textarea,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import {
  JOURNEY_STEP_ORDER,
  useAddJourneyNote,
  type CustomerJourney,
} from '@/api/customer-journey';
import { stepLabel } from './sale-process-helpers';
import { errorMessage } from './sale-process-error';

/**
 * Add a note to a journey (`POST .../note`). The step defaults to the journey's
 * current step; the note text is required. On success the mutation invalidates
 * the journeys query so the timeline refreshes.
 */
export function AddJourneyNoteDialog({
  customerId,
  journey,
  open,
  onOpenChange,
  defaultStep: defaultStepProp,
}: {
  customerId: number;
  journey: CustomerJourney;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-select a step (a board column pins the column's step); else the journey's step. */
  defaultStep?: string;
}) {
  const fallbackStep = JOURNEY_STEP_ORDER.includes(journey.customer_journey_step as never)
    ? String(journey.customer_journey_step)
    : JOURNEY_STEP_ORDER[0];
  const defaultStep = defaultStepProp ?? fallbackStep;
  const [note, setNote] = useState('');
  const [step, setStep] = useState(defaultStep);
  const mutation = useAddJourneyNote(customerId, journey.customer_journey_id);

  const close = (next: boolean) => {
    if (mutation.isPending) return;
    if (!next) {
      setNote('');
      setStep(defaultStep);
      mutation.reset();
    }
    onOpenChange(next);
  };

  const submit = () => {
    if (!note.trim()) return;
    mutation.mutate(
      {
        customer_journey_note: note.trim(),
        customer_journey_note_step: step,
        customer_journey_note_step_status: 'processing',
        customer_journey_notes_time: new Date().toISOString(),
      },
      { onSuccess: () => close(false) },
    );
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Add note</DialogTitle>
          <DialogDescription>Log a follow-up under a pipeline step.</DialogDescription>
        </DialogHeader>
        <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {mutation.isError ? (
            <Alert tone="danger" title="Could not save note">{errorMessage(mutation.error)}</Alert>
          ) : null}
          <Field label="Step">
            {(p) => (
              <Select value={step} onValueChange={setStep}>
                <SelectTrigger {...p}>
                  <SelectValue placeholder="Select step" />
                </SelectTrigger>
                <SelectContent>
                  {JOURNEY_STEP_ORDER.map((s) => (
                    <SelectItem key={s} value={s}>
                      {stepLabel(s)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
          <Field label="Note" required>
            {(p) => (
              <Textarea
                {...p}
                rows={5}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Enter note content…"
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" disabled={mutation.isPending} onClick={() => close(false)}>
            Cancel
          </Button>
          <Button variant="primary" loading={mutation.isPending} disabled={!note.trim()} onClick={submit}>
            Add note
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AddJourneyNoteDialog;
